import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import { createCoupon, getCoupons, updateCoupon, deleteCoupon } from '../../services/couponService';
import { uploadImage } from '../../services/productService';
import { toast } from 'react-toastify';
import { useToast } from '../../contexts/ToastContext';
import formatPrice from '../../utils/formatPrice';
import '../../css/admincoupons.css';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import { Table } from '../../components/Table';
import {
  Ticket,
  Plus,
  Search,
  X,
  Copy,
  Check,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  CheckCircle2,
  Tag,
  Sparkles,
  UploadCloud,
  FileImage
} from 'lucide-react';

const AdminCouponsPage = () => {
  const { confirm } = useToast();
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState('all');
  const [copiedCode, setCopiedCode] = useState(null);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    code: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderValue: '',
    maxDiscount: '',
    usageLimit: '',
    expiryDate: '',
    isActive: true
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setIsLoading(true);
      const data = await getCoupons();
      setCoupons(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if coupon is expired
  const isCouponExpired = useCallback((expiryDate) => {
    if (!expiryDate) return false;
    const exp = new Date(expiryDate);
    if (isNaN(exp.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return exp < today;
  }, []);

  // Format expiry display
  const formatExpiry = (dateStr) => {
    if (!dateStr) return 'No Expiry';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Copy code to clipboard
  const handleCopyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      toast.success(`Copied code "${code}" to clipboard!`);
      setTimeout(() => setCopiedCode(null), 2000);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Toggle active status in 1-click
  const handleToggleActive = async (coupon) => {
    const updatedStatus = !coupon.isActive;
    try {
      await updateCoupon(coupon._id, { ...coupon, isActive: updatedStatus });
      setCoupons(prev => prev.map(c => c._id === coupon._id ? { ...c, isActive: updatedStatus } : c));
      toast.success(`Coupon "${coupon.code}" is now ${updatedStatus ? 'Active' : 'Inactive'}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Open drawer for creating
  const handleOpenCreate = () => {
    setEditingCoupon(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      code: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderValue: '',
      maxDiscount: '',
      usageLimit: '',
      expiryDate: '',
      isActive: true
    });
    setIsDrawerOpen(true);
  };

  // Open drawer for editing
  const handleOpenEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      title: coupon.title || '',
      description: coupon.description || '',
      image: coupon.image || '',
      code: coupon.code || '',
      discountType: coupon.discountType || 'percentage',
      discountValue: coupon.discountValue !== undefined ? coupon.discountValue : '',
      minOrderValue: coupon.minOrderValue !== undefined ? coupon.minOrderValue : '',
      maxDiscount: coupon.maxDiscount !== undefined ? coupon.maxDiscount : '',
      usageLimit: coupon.usageLimit !== undefined ? coupon.usageLimit : '',
      expiryDate: coupon.expiryDate ? coupon.expiryDate.split('T')[0] : '',
      isActive: coupon.isActive !== undefined ? coupon.isActive : true
    });
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    if (isSubmitting) return;
    setIsDrawerOpen(false);
    setEditingCoupon(null);
  };

  // Close drawer on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isDrawerOpen) {
        handleCloseDrawer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, isSubmitting]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === 'checkbox' ? checked : value;
    if (name === 'code' && typeof val === 'string') {
      val = val.toUpperCase().replace(/\s+/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setIsUploading(true);
        const imageUrl = await uploadImage(file);
        setFormData(prev => ({ ...prev, image: imageUrl }));
        toast.success('Banner uploaded successfully');
      } catch (error) {
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a coupon title');
      return;
    }
    if (!formData.code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    if (!formData.discountValue || Number(formData.discountValue) <= 0) {
      toast.error('Please provide a valid discount value');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        discountValue: Number(formData.discountValue),
        minOrderValue: formData.minOrderValue ? Number(formData.minOrderValue) : 0,
        maxDiscount: formData.maxDiscount ? Number(formData.maxDiscount) : undefined,
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : undefined,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null
      };

      if (editingCoupon) {
        await updateCoupon(editingCoupon._id, payload);
        toast.success(`Coupon "${payload.code}" updated successfully`);
      } else {
        await createCoupon(payload);
        toast.success(`Coupon "${payload.code}" created successfully`);
      }
      await loadCoupons();
      handleCloseDrawer();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save coupon';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this coupon?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await deleteCoupon(id);
      toast.success('Coupon deleted successfully');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  // KPI Calculations
  const totalCouponsCount = coupons.length;
  const activeCouponsCount = coupons.filter(c => c.isActive && !isCouponExpired(c.expiryDate)).length;
  const expiredOrInactiveCount = coupons.filter(c => !c.isActive || isCouponExpired(c.expiryDate)).length;

  // Filter tab counts
  const tabCounts = useMemo(() => {
    return {
      all: coupons.length,
      active: coupons.filter(c => c.isActive && !isCouponExpired(c.expiryDate)).length,
      expired: coupons.filter(c => isCouponExpired(c.expiryDate)).length,
      percentage: coupons.filter(c => c.discountType === 'percentage').length,
      fixed: coupons.filter(c => c.discountType === 'fixed').length
    };
  }, [coupons, isCouponExpired]);

  // Filtered dataset for Table
  const filteredCoupons = useMemo(() => {
    let list = [...coupons];

    // Filter by tab
    if (activeFilterTab === 'active') {
      list = list.filter(c => c.isActive && !isCouponExpired(c.expiryDate));
    } else if (activeFilterTab === 'expired') {
      list = list.filter(c => isCouponExpired(c.expiryDate));
    } else if (activeFilterTab === 'percentage') {
      list = list.filter(c => c.discountType === 'percentage');
    } else if (activeFilterTab === 'fixed') {
      list = list.filter(c => c.discountType === 'fixed');
    }

    // Filter by search query
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(c =>
        (c.code && c.code.toLowerCase().includes(q)) ||
        (c.title && c.title.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q))
      );
    }

    return list;
  }, [coupons, activeFilterTab, searchTerm, isCouponExpired]);

  // Table Columns Definition
  const columns = useMemo(
    () => [
      {
        Header: 'Coupon Name',
        id: 'coupon',
        accessor: (c) => c.title,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="aco-coupon-cell">
              {c.image ? (
                <img src={mediaUrl(c.image)} alt={c.title} className="aco-coupon-avatar" />
              ) : (
                <div className="aco-coupon-avatar">
                  <Ticket size={20} />
                </div>
              )}
              <div className="aco-coupon-meta">
                <span className="aco-coupon-title" title={c.title}>{c.title}</span>
                {c.description && (
                  <span className="aco-coupon-desc" title={c.description}>{c.description}</span>
                )}
              </div>
            </div>
          );
        }
      },
      {
        Header: 'Code',
        id: 'code',
        accessor: 'code',
        Cell: ({ row }) => {
          const c = row.original;
          const isCopied = copiedCode === c.code;
          return (
            <div className="aco-code-wrap">
              <span className="aco-code-pill">{c.code}</span>
              <button
                type="button"
                className={`aco-btn-copy ${isCopied ? 'copied' : ''}`}
                onClick={() => handleCopyCode(c.code)}
                title={isCopied ? 'Copied to clipboard!' : 'Copy Code'}
                aria-label={`Copy coupon code ${c.code}`}
              >
                {isCopied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            </div>
          );
        }
      },
      {
        Header: 'Discount',
        id: 'discount',
        accessor: (c) => c.discountValue,
        Cell: ({ row }) => {
          const c = row.original;
          if (c.discountType === 'percentage') {
            return (
              <div className="aco-discount-cell">
                <span className="aco-discount-value">{c.discountValue}% OFF</span>
                <span className="aco-discount-cap">
                  {c.maxDiscount ? `Max cap: ${formatPrice(c.maxDiscount)}` : 'No max cap'}
                </span>
              </div>
            );
          }
          return (
            <div className="aco-discount-cell">
              <span className="aco-discount-value">{formatPrice(c.discountValue)} OFF</span>
              <span className="aco-discount-cap">Flat discount</span>
            </div>
          );
        }
      },
      {
        Header: 'Criteria & Usage',
        id: 'criteria',
        accessor: (c) => c.minOrderValue || 0,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="aco-criteria-cell">
              <span className="aco-criteria-min">
                Min Spend: <strong>{c.minOrderValue > 0 ? formatPrice(c.minOrderValue) : '₹0'}</strong>
              </span>
              <span className="aco-usage-badge">
                Usage: {c.usedCount || 0} / {c.usageLimit ? `${c.usageLimit}` : '∞'}
              </span>
            </div>
          );
        }
      },
      {
        Header: 'Expiry',
        id: 'expiry',
        accessor: (c) => (c.expiryDate ? new Date(c.expiryDate).getTime() : 0),
        Cell: ({ row }) => {
          const c = row.original;
          const expired = isCouponExpired(c.expiryDate);
          return (
            <div className="aco-expiry-cell">
              <span className="aco-expiry-date">
                <Calendar size={13} className="aco-expiry-icon" />
                {formatExpiry(c.expiryDate)}
              </span>
              {c.expiryDate && (
                <span className={`aco-expiry-tag ${expired ? 'expired' : 'valid'}`}>
                  {expired ? 'Expired' : 'Valid'}
                </span>
              )}
            </div>
          );
        }
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: (c) => (c.isActive ? 1 : 0),
        Cell: ({ row }) => {
          const c = row.original;
          const expired = isCouponExpired(c.expiryDate);
          const statusClass = expired ? 'expired' : c.isActive ? 'active' : 'inactive';
          const statusLabel = expired ? 'Expired' : c.isActive ? 'Active' : 'Inactive';
          return (
            <button
              type="button"
              className={`aco-status-btn ${statusClass}`}
              onClick={() => handleToggleActive(c)}
              title="Click to toggle active status"
            >
              <span className="aco-status-dot" />
              {statusLabel}
            </button>
          );
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="aco-actions">
              <button
                type="button"
                className="aco-btn-edit"
                onClick={() => handleOpenEdit(c)}
                title="Edit Coupon"
              >
                <Pencil size={13} />
                Edit
              </button>
              <button
                type="button"
                className="aco-btn-danger"
                onClick={() => handleDelete(c._id)}
                title="Delete Coupon"
              >
                <Trash2 size={13} />
                Delete
              </button>
            </div>
          );
        }
      }
    ],
    [copiedCode, isCouponExpired]
  );

  // Formatted Live Discount Display for Drawer Preview Ticket
  const previewDiscountText = useMemo(() => {
    if (formData.discountValue && Number(formData.discountValue) > 0) {
      return formData.discountType === 'percentage'
        ? `${formData.discountValue}% OFF`
        : `${formatPrice(formData.discountValue)} OFF`;
    }
    return formData.discountType === 'percentage' ? '15% OFF' : '₹150 OFF';
  }, [formData.discountValue, formData.discountType]);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content aco-page">
        <AdminHeader title="Coupons" />

        <div className="aco-container">
          {/* Page heading */}
          <div className="aco-header-card">
            <div className="aco-title-group">
              <h1 className="aco-title">Coupons</h1>
              <p className="aco-subtitle">
                Configure promotional codes, seasonal discounts, and customer incentives
              </p>
            </div>

            <div className="aco-heading-actions">
              <span className="aco-title-badge">{coupons.length} Total</span>
              <button
                type="button"
                className="aco-btn-primary"
                onClick={handleOpenCreate}
              >
                <Plus size={16} />
                <span>Create Coupon</span>
              </button>
            </div>
          </div>

          {/* Summary cards */}
          <div className="aco-kpi-grid">
            <div className="aco-kpi-card">
              <div className="aco-kpi-top">
                <span className="aco-kpi-label">Total Coupons</span>
                <span className="aco-kpi-icon-wrap">
                  <Ticket size={16} />
                </span>
              </div>
              <span className="aco-kpi-val">{totalCouponsCount}</span>
              <span className="aco-kpi-sub">Configured in store catalog</span>
            </div>

            <div className="aco-kpi-card">
              <div className="aco-kpi-top">
                <span className="aco-kpi-label">Active & Valid</span>
                <span className="aco-kpi-icon-wrap">
                  <CheckCircle2 size={16} />
                </span>
              </div>
              <span className="aco-kpi-val">{activeCouponsCount}</span>
              <span className="aco-kpi-sub">Ready for checkout usage</span>
            </div>

            <div className="aco-kpi-card">
              <div className="aco-kpi-top">
                <span className="aco-kpi-label">Expired / Disabled</span>
                <span className="aco-kpi-icon-wrap">
                  <Clock size={16} />
                </span>
              </div>
              <span className="aco-kpi-val">{expiredOrInactiveCount}</span>
              <span className="aco-kpi-sub">Past expiry date or turned off</span>
            </div>
          </div>

          {/* Search Toolbar & Filter Tabs */}
          <div className="aco-controls-card">
            <div className="aco-toolbar-row">
              <div className="aco-search-box">
                <Search size={15} className="aco-search-icon" />
                <input
                  type="text"
                  placeholder="Search by code, title, or terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="aco-search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="aco-search-clear"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="aco-tabs-wrapper">
                <button
                  type="button"
                  className={`aco-tab-pill ${activeFilterTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilterTab('all')}
                >
                  <span>All</span>
                  <span className="aco-tab-count">{tabCounts.all}</span>
                </button>
                <button
                  type="button"
                  className={`aco-tab-pill ${activeFilterTab === 'active' ? 'active' : ''}`}
                  onClick={() => setActiveFilterTab('active')}
                >
                  <span>Active</span>
                  <span className="aco-tab-count">{tabCounts.active}</span>
                </button>
                <button
                  type="button"
                  className={`aco-tab-pill ${activeFilterTab === 'expired' ? 'active' : ''}`}
                  onClick={() => setActiveFilterTab('expired')}
                >
                  <span>Expired</span>
                  <span className="aco-tab-count">{tabCounts.expired}</span>
                </button>
                <button
                  type="button"
                  className={`aco-tab-pill ${activeFilterTab === 'percentage' ? 'active' : ''}`}
                  onClick={() => setActiveFilterTab('percentage')}
                >
                  <span>Percentage (%)</span>
                  <span className="aco-tab-count">{tabCounts.percentage}</span>
                </button>
                <button
                  type="button"
                  className={`aco-tab-pill ${activeFilterTab === 'fixed' ? 'active' : ''}`}
                  onClick={() => setActiveFilterTab('fixed')}
                >
                  <span>Fixed (₹)</span>
                  <span className="aco-tab-count">{tabCounts.fixed}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Coupons Data Table */}
          {filteredCoupons.length === 0 ? (
            <div className="aco-empty-card">
              <div className="aco-empty-icon">
                <Ticket size={36} />
              </div>
              <h3>No Coupons Found</h3>
              <p>
                {searchTerm || activeFilterTab !== 'all'
                  ? 'No coupons match your current filter or search criteria.'
                  : 'Start rewarding customers by creating promotional discount codes.'}
              </p>
              {searchTerm || activeFilterTab !== 'all' ? (
                <button
                  type="button"
                  className="aco-empty-clear-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveFilterTab('all');
                  }}
                >
                  Clear Filters
                </button>
              ) : (
                <button
                  type="button"
                  className="aco-empty-create-btn"
                  onClick={handleOpenCreate}
                >
                  <Plus size={16} /> Create Coupon
                </button>
              )}
            </div>
          ) : (
            <Table columns={columns} data={filteredCoupons} show color="#4a3d36" />
          )}
        </div>

        {/* Slide-Out Coupon Drawer with Sticky Footer */}
        {isDrawerOpen && (
          <div className="aco-drawer-backdrop" onClick={handleCloseDrawer}>
            <div className="aco-drawer" onClick={(e) => e.stopPropagation()}>
              {/* Drawer Header */}
              <div className="aco-drawer-header">
                <div className="aco-drawer-header-left">
                  <span className="aco-drawer-eyebrow">
                    {editingCoupon ? 'Modify Promotion' : 'New Promotion'}
                  </span>
                  <h2 className="aco-drawer-title">
                    <Ticket size={18} className="aco-drawer-title-icon" />
                    {editingCoupon ? `Edit: ${editingCoupon.code}` : 'Create Coupon'}
                  </h2>
                </div>
                <button
                  type="button"
                  className="aco-drawer-close"
                  onClick={handleCloseDrawer}
                  aria-label="Close drawer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Wrapper encapsulating Scrollable Body + Fixed Sticky Footer */}
              <form onSubmit={handleSubmit} className="aco-drawer-form-wrapper">
                {/* Scrollable Drawer Body */}
                <div className="aco-drawer-body">
                  {/* Live Real-Time Preview Ticket (Never Squashed) */}
                  <div className="aco-preview-card">
                    <div className="aco-preview-badge-row">
                      <span className="aco-preview-discount">
                        {previewDiscountText}
                      </span>
                      <span className="aco-preview-chip">
                        {formData.discountType === 'percentage' ? 'Percentage' : 'Flat Discount'}
                      </span>
                    </div>
                    <p className="aco-preview-title">
                      {formData.title || 'Special Promotion Title'}
                    </p>
                    <div className="aco-preview-code-row">
                      <span className="aco-preview-code">
                        {formData.code || 'COUPONCODE'}
                      </span>
                      <Sparkles size={15} className="aco-preview-sparkle" />
                    </div>
                    <p className="aco-preview-rules">
                      Min spend {formData.minOrderValue ? formatPrice(formData.minOrderValue) : '₹0'}
                      {' • '}
                      {formData.expiryDate ? `Valid till ${formatExpiry(formData.expiryDate)}` : 'No expiry'}
                    </p>
                  </div>

                  {/* Card 1: Basic Information */}
                  <div className="aco-drawer-card">
                    <h3 className="aco-drawer-card-title">General Details</h3>
                    <div className="aco-form-group">
                      <label>
                        Coupon Title <span className="aco-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        placeholder="e.g. Festive Special Offer"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="aco-form-group">
                      <label>Description / Terms</label>
                      <textarea
                        name="description"
                        rows="2"
                        placeholder="e.g. Valid on all traditional silk collections above ₹999..."
                        value={formData.description}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="aco-form-group">
                      <label>Promotional Banner (Optional)</label>
                      <div className="aco-upload-dropzone">
                        {formData.image ? (
                          <img
                            src={mediaUrl(formData.image)}
                            alt="Banner Preview"
                            className="aco-upload-preview"
                          />
                        ) : (
                          <div className="aco-upload-icon-box">
                            <FileImage size={24} />
                          </div>
                        )}
                        <div className="aco-upload-meta">
                          <label className="aco-upload-label">
                            <UploadCloud size={15} />
                            <span>{isUploading ? 'Uploading Banner...' : formData.image ? 'Change Banner Image' : 'Upload Promotional Banner'}</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              disabled={isUploading}
                            />
                          </label>
                          <span className="aco-upload-hint">PNG, JPG, WEBP • Max 2MB (800x400 recommended)</span>
                          {formData.image && (
                            <button
                              type="button"
                              className="aco-upload-remove-btn"
                              onClick={handleRemoveImage}
                            >
                              Remove banner
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Discount & Code */}
                  <div className="aco-drawer-card">
                    <h3 className="aco-drawer-card-title">Promo Code & Discount</h3>
                    <div className="aco-form-row">
                      <div className="aco-form-group">
                        <label>
                          Coupon Code <span className="aco-required">*</span>
                        </label>
                        <div className="aco-code-input-wrap">
                          <Tag size={15} className="aco-code-input-icon" />
                          <input
                            type="text"
                            name="code"
                            placeholder="e.g. FESTIVE20"
                            value={formData.code}
                            onChange={handleInputChange}
                            required
                            className="aco-code-input"
                          />
                        </div>
                      </div>

                      <div className="aco-form-group">
                        <label>Discount Type</label>
                        <select
                          name="discountType"
                          value={formData.discountType}
                          onChange={handleInputChange}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (₹)</option>
                        </select>
                      </div>
                    </div>

                    <div className="aco-form-row">
                      <div className="aco-form-group">
                        <label>
                          Discount Value <span className="aco-required">*</span>
                        </label>
                        <input
                          type="number"
                          name="discountValue"
                          min="1"
                          placeholder={formData.discountType === 'percentage' ? 'e.g. 15 (%)' : 'e.g. 150 (₹)'}
                          value={formData.discountValue}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="aco-form-group">
                        <label>Max Discount Cap (₹)</label>
                        <input
                          type="number"
                          name="maxDiscount"
                          placeholder="e.g. 500 (Optional)"
                          value={formData.maxDiscount}
                          onChange={handleInputChange}
                          disabled={formData.discountType === 'fixed'}
                        />
                        <span className="aco-input-hint">Leave blank if no maximum cap</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Spending Criteria & Limits */}
                  <div className="aco-drawer-card">
                    <h3 className="aco-drawer-card-title">Rules & Restrictions</h3>
                    <div className="aco-form-row">
                      <div className="aco-form-group">
                        <label>Min Order Spend (₹)</label>
                        <input
                          type="number"
                          name="minOrderValue"
                          min="0"
                          placeholder="e.g. 499 (0 for none)"
                          value={formData.minOrderValue}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="aco-form-group">
                        <label>Total Usage Limit</label>
                        <input
                          type="number"
                          name="usageLimit"
                          min="1"
                          placeholder="e.g. 100 (Optional)"
                          value={formData.usageLimit}
                          onChange={handleInputChange}
                        />
                        <span className="aco-input-hint">Leave blank for unlimited uses</span>
                      </div>
                    </div>

                    <div className="aco-form-group">
                      <label>Expiration Date</label>
                      <input
                        type="date"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Card 4: Status Active Toggle */}
                  <div className="aco-drawer-card">
                    <div className="aco-switch-row">
                      <div className="aco-switch-meta">
                        <p className="aco-switch-title">Active & Live in Store</p>
                        <p className="aco-switch-desc">
                          When enabled, eligible customers can apply this promo code at checkout
                        </p>
                      </div>
                      <label className="aco-switch-label">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <span className="aco-switch-slider" />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Sticky Drawer Footer (Permanently Pinned to Bottom) */}
                <div className="aco-drawer-footer">
                  <button
                    type="button"
                    className="aco-btn-secondary"
                    onClick={handleCloseDrawer}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="aco-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span>Saving...</span>
                    ) : (
                      <>
                        <Check size={16} />
                        <span>{editingCoupon ? 'Update Coupon' : 'Create Coupon'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCouponsPage;
